import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Plus, Edit, Power, ChevronLeft, Check, X, Upload, Trash2, MessageCircle, Trash, Phone, LogIn } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import CreateUserWizard from '../../components/admin/CreateUserWizard';
import BulkUploadModal from '../../components/admin/BulkUploadModal';
import BulkNotifyModal from '../../components/admin/BulkNotifyModal';
import PasswordConfirmModal from '../../components/admin/PasswordConfirmModal';
import EditUserModal from '../../components/admin/EditUserModal';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - User Management Page (Phase 3A)
 * Admin-only page for managing system users
 * Features: List, filter, create, update role, activate/deactivate
 */

const UserManagementPage = () => {
  const t = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkNotify, setShowBulkNotify] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    q: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(users.map(u => u.user_id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(t('admin.users.bulk.confirmDelete').replace('{count}', selectedUsers.length))) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/bulk`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ user_ids: selectedUsers })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(t('admin.users.success.bulkDeleted').replace('{count}', result.deleted_count));
        setSelectedUsers([]);
        
        // Auto-switch to "active" filter to hide deactivated users
        setFilters(prev => ({ ...prev, status: 'active' }));
      } else {
        toast.error('Failed to delete users');
      }
    } catch (err) {
      console.error('Error deleting users:', err);
      toast.error('Failed to delete users');
    }
  };

  const handlePermanentDelete = async () => {
    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/bulk`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            user_ids: selectedUsers,
            mode: 'delete'  // Hard delete mode
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(t('admin.users.success.bulkPermanentDeleted').replace('{count}', result.deleted_count));
        setSelectedUsers([]);
        
        // Refresh user list - permanently deleted users won't appear anywhere
        fetchUsers();
      } else {
        toast.error('Failed to permanently delete users');
      }
    } catch (err) {
      console.error('Error permanently deleting users:', err);
      toast.error('Failed to permanently delete users');
    }
  };

  const handleBulkNotify = () => {
    const selectedUserData = users.filter(u => selectedUsers.includes(u.user_id));
    setShowBulkNotify(true);
  };

  const handleImpersonate = async (targetUser) => {
    try {
      setImpersonating(targetUser.user_id);
            
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/${targetUser.user_id}/impersonate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Save current admin token for return
        secureStorage.setItem('nexus_admin_return_token', secureStorage.getItem('nexus_access_token'));
        secureStorage.setItem('nexus_admin_return_refresh', secureStorage.getItem('nexus_refresh_token') || '');
        secureStorage.setItem('nexus_admin_return_user', secureStorage.getItem('nexus_user'));
        secureStorage.setItem('nexus_impersonating', 'true');
        
        // Switch to impersonated user session
        login({
          access_token: data.access_token,
          user: {
            ...data.user,
            impersonated_by: data.user.impersonated_by
          }
        });
        
        toast.success(`${t('admin.users.impersonate.success') || 'Now viewing as'} ${targetUser.email}`);
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to impersonate user');
      }
    } catch (err) {
      console.error('Error impersonating user:', err);
      toast.error('Failed to impersonate user');
    } finally {
      setImpersonating(null);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
            const params = new URLSearchParams();
      
      if (filters.role !== 'all') params.append('role', filters.role);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.q) params.append('q', filters.q);
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Error ${response.status}`;
        console.error('Failed to fetch users:', response.status, errorMsg);
        toast.error(`Failed to load users: ${errorMsg}`);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Network error loading users. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole, oldRole) => {
    if (!window.confirm(t('admin.users.confirmRoleChange').replace('{oldRole}', oldRole).replace('{newRole}', newRole))) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/${userId}/role`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: newRole })
        }
      );

      if (response.ok) {
        toast.success(t('admin.users.successRole'));
        fetchUsers();
      } else {
        toast.error(t('admin.users.errorRole'));
      }
    } catch (err) {
      console.error('Error updating role:', err);
      toast.error(t('admin.users.errorRole'));
    }
  };

  const updateUserStatus = async (userId, newStatus, currentStatus) => {
    const confirmMessage = newStatus ? t('admin.users.confirmActivate') : t('admin.users.confirmDeactivate');
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/users/${userId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_active: newStatus })
        }
      );

      if (response.ok) {
        toast.success(t('admin.users.successStatus'));
        fetchUsers();
      } else {
        toast.error(t('admin.users.errorStatus'));
      }
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error(t('admin.users.errorStatus'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'admin': 'bg-purple-100 text-purple-800',
      'management_functional': 'bg-blue-100 text-blue-800',
      'supervisor': 'bg-green-100 text-green-800',
      'executor': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: EMZI_COLORS.background
    }}>
      {/* Fixed Header - EMZI CI Standard */}
      <div style={{
        background: EMZI_COLORS.primary,
        padding: '20px',
        color: 'white',
        boxShadow: EMZI_SHADOWS.header,
        flexShrink: 0
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              padding: '8px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'white'
            }}>
              <Users size={28} strokeWidth={2} />
              {t('admin.users.title')}
            </h1>
            <p style={{
              fontSize: '14px',
              opacity: 0.9,
              margin: '4px 0 0 0',
              color: 'white'
            }}>
              {t('admin.users.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Action Bar */}
      <div style={{
        padding: '16px 20px',
        background: EMZI_COLORS.primary,
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        flexShrink: 0,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setShowBulkUpload(true)}
          style={{
            flex: '1',
            minWidth: '140px',
            padding: '12px 20px',
            background: 'transparent',
            color: 'white',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Upload size={18} />
          {t('admin.users.bulk.uploadButton')}
        </button>

        <button
          onClick={() => setShowWizard(true)}
          style={{
            flex: '1',
            minWidth: '140px',
            padding: '12px 20px',
            background: 'transparent',
            color: 'white',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Plus size={18} />
          {t('admin.users.createUser')}
        </button>
      </div>

      {/* Scrollable Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth',
        padding: '20px',
        paddingBottom: '100px'
      }}>

      {/* Filters */}
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.md,
        padding: '20px',
        marginBottom: '20px',
        boxShadow: EMZI_SHADOWS.card,
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        {/* Role Filter */}
        <select
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          style={{
            flex: '1',
            minWidth: '150px',
            padding: '10px 12px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">{t('admin.users.allRoles')}</option>
          <option value="admin">{t('admin.users.roles.admin')}</option>
          <option value="management_functional">{t('admin.users.roles.management_functional')}</option>
          <option value="supervisor">{t('admin.users.roles.supervisor')}</option>
          <option value="executor">{t('admin.users.roles.executor')}</option>
        </select>

        {/* Status Filter */}
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{
            flex: '1',
            minWidth: '150px',
            padding: '10px 12px',
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.sm,
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">{t('admin.users.allStatus')}</option>
          <option value="active">{t('admin.users.active')}</option>
          <option value="inactive">{t('admin.users.inactive')}</option>
        </select>

        {/* Search */}
        <div style={{ flex: '2', minWidth: '200px', position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: EMZI_COLORS.textSecondary
          }} />
          <input
            type="text"
            placeholder={t('admin.users.searchPlaceholder')}
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedUsers.length > 0 && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'linear-gradient(135deg, #0055FF 0%, #0044CC 100%)',
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: EMZI_RADIUS.md,
          boxShadow: EMZI_SHADOWS.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            color: 'white'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '6px 12px',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '13px',
              fontWeight: '700'
            }}>
              {selectedUsers.length} selected
            </div>
            <button
              onClick={() => setSelectedUsers([])}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            flexWrap: 'wrap',
            width: '100%'
          }}>
            <button
              onClick={handleBulkNotify}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                minHeight: '56px',
                flex: '1 1 calc(50% - 4px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
            >
              <MessageCircle size={16} />
              <span>WhatsApp</span>
            </button>
            <button
              onClick={handleBulkDelete}
              style={{
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                minHeight: '56px',
                flex: '1 1 calc(50% - 4px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
            >
              <Trash2 size={16} />
              <span>Deactivate</span>
            </button>
            <button
              onClick={() => setShowPasswordConfirm(true)}
              style={{
                background: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '12px',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                minHeight: '56px',
                flex: '1 1 100%',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
              }}
            >
              <Trash size={16} />
              <span>Permanent Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* Users Table - Scrollable */}
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.md,
        boxShadow: EMZI_SHADOWS.card,
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 400px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            color: EMZI_COLORS.textSecondary
          }}>
            {t('admin.users.loading')}
          </div>
        ) : users.length === 0 ? (
          <div style={{
            padding: '60px',
            textAlign: 'center'
          }}>
            <Users size={48} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
            <div style={{
              fontSize: '16px',
              fontWeight: '500',
              color: EMZI_COLORS.textSecondary
            }}>
              {t('admin.users.noData')}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'white'
              }}>
                <tr style={{
                  background: EMZI_COLORS.background,
                  borderBottom: `2px solid ${EMZI_COLORS.border}`
                }}>
                  <th style={{
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    width: '50px'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={handleSelectAll}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                  </th>
                  <th style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.users.fullName')}
                  </th>
                  <th style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.users.email')}
                  </th>
                  <th style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.users.phoneNumber')}
                  </th>
                  <th style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.users.role')}
                  </th>
                  <th style={{
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.users.status')}
                  </th>
                  <th style={{
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textSecondary,
                    textTransform: 'uppercase'
                  }}>
                    {t('admin.users.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr
                    key={user.user_id}
                    style={{
                      borderBottom: `1px solid ${EMZI_COLORS.border}`,
                      background: selectedUsers.includes(user.user_id) ? '#eff6ff' : (index % 2 === 0 ? 'white' : EMZI_COLORS.background),
                      transition: 'background 0.2s'
                    }}
                  >
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.user_id)}
                        onChange={() => handleSelectUser(user.user_id)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: EMZI_COLORS.textPrimary
                      }}>
                        {user.full_name}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: EMZI_COLORS.textSecondary,
                        marginTop: '2px'
                      }}>
                        {t('admin.users.groupCount')}: {user.group_count || 0}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{
                        fontSize: '14px',
                        color: EMZI_COLORS.textPrimary
                      }}>
                        {user.email}
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      {user.phone_e164 ? (
                        <a
                          href={`https://wa.me/${user.phone_e164.replace('+', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px',
                            color: '#10b981',
                            textDecoration: 'none',
                            padding: '4px 8px',
                            borderRadius: EMZI_RADIUS.sm,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <Phone size={16} />
                          {user.phone_e164}
                        </a>
                      ) : (
                        <span style={{
                          fontSize: '13px',
                          color: EMZI_COLORS.textSecondary,
                          fontStyle: 'italic'
                        }}>
                          No phone
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <select
                        value={user.role}
                        onChange={(e) => updateUserRole(user.user_id, e.target.value, user.role)}
                        style={{
                          padding: '6px 10px',
                          border: `1px solid ${EMZI_COLORS.border}`,
                          borderRadius: EMZI_RADIUS.sm,
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          background: 'white'
                        }}
                      >
                        <option value="admin">{t('admin.users.roles.admin')}</option>
                        <option value="management_functional">{t('admin.users.roles.management_functional')}</option>
                        <option value="supervisor">{t('admin.users.roles.supervisor')}</option>
                        <option value="executor">{t('admin.users.roles.executor')}</option>
                      </select>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: user.is_active ? '#10b981' : '#ef4444',
                        color: 'white'
                      }}>
                        {user.is_active ? t('admin.users.active') : t('admin.users.inactive')}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          data-testid={`edit-user-${user.user_id}`}
                          onClick={() => {
                            setEditingUser(user);
                            setShowEditModal(true);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#0ea5e9',
                            color: 'white',
                            border: 'none',
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          data-testid={`impersonate-user-${user.user_id}`}
                          onClick={() => handleImpersonate(user)}
                          disabled={impersonating === user.user_id}
                          style={{
                            padding: '8px 16px',
                            background: '#7c3aed',
                            color: 'white',
                            border: 'none',
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: impersonating === user.user_id ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: impersonating === user.user_id ? 0.6 : 1
                          }}
                        >
                          <LogIn size={14} />
                          {impersonating === user.user_id ? '...' : (t('admin.users.loginAs') || 'Login As')}
                        </button>
                        <button
                          onClick={() => updateUserStatus(user.user_id, !user.is_active, user.is_active)}
                          style={{
                            padding: '8px 16px',
                            background: user.is_active ? '#fef3c7' : '#d1fae5',
                            color: user.is_active ? '#92400e' : '#065f46',
                            border: 'none',
                            borderRadius: EMZI_RADIUS.sm,
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <Power size={14} />
                          {user.is_active ? t('admin.users.deactivate') : t('admin.users.activate')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      </div> {/* End Scrollable Content */}

      {/* Create User Wizard */}
      {showWizard && (
        <CreateUserWizard
          onClose={() => setShowWizard(false)}
          onSuccess={() => {
            setShowWizard(false);
            fetchUsers();
            toast.success(t('admin.users.successCreate'));
          }}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={(result) => {
            if (result.stats.created > 0) {
              fetchUsers();
            }
          }}
        />
      )}

      {/* Bulk Notify Modal */}
      {showBulkNotify && (
        <BulkNotifyModal
          isOpen={showBulkNotify}
          onClose={() => setShowBulkNotify(false)}
          selectedUsers={users.filter(u => selectedUsers.includes(u.user_id))}
        />
      )}

      {/* Password Confirm Modal for Permanent Delete */}
      {showPasswordConfirm && (
        <PasswordConfirmModal
          isOpen={showPasswordConfirm}
          onClose={() => setShowPasswordConfirm(false)}
          onConfirm={handlePermanentDelete}
          title={t('admin.users.permanentDelete.confirmTitle')}
          message={t('admin.users.permanentDelete.warning')}
          confirmPassword="emzi4111"
          selectedCount={selectedUsers.length}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <EditUserModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onSuccess={() => {
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagementPage;